import auth from '@/services/auth';
import { getBootcampBySlug, getActiveBootcamps } from '@/services/bootcamp';
import db from '@/services/db';
import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  FirestoreDataConverter,
  Query,
  QueryDocumentSnapshot,
  Timestamp,
  collection,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useCollection } from 'react-firebase-hooks/firestore';
import { useNavigate, useParams } from 'react-router-dom';

import { Attendee } from '@/types/attendee';
import type { Bootcamp } from '@/types/bootcamp';
import { Button, Typography } from '@ford/ford-ui-components';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import moment from 'moment-timezone';
import '../in/modal-styles.css'; // Import custom modal styling
import styles from './_index.module.css';

// Extend Attendee interface to include document reference and ID
interface CheckAttendee extends Omit<Attendee, 'productName'> {
  id: string;
  ref: DocumentReference<DocumentData>;
  eventDate?: Date;
  moduleName?: string;
  productName?: string;
  howLearn: string;
}

const checkUserConverter: FirestoreDataConverter<CheckAttendee> = {
  toFirestore(checkAttendee: CheckAttendee): DocumentData {
    return {
      cartID: checkAttendee.cartID,
      eventID: checkAttendee.eventID,
      productName: checkAttendee.productName,
      eventTime: checkAttendee.eventTime,
      emailSent: checkAttendee.emailSent,
      waiverComplete: checkAttendee.waiverComplete,
      firstName: checkAttendee.firstName,
      lastName: checkAttendee.lastName,
      email: checkAttendee.email,
      phone: checkAttendee.phone,
      type: checkAttendee.type,
      fordOwner: checkAttendee.fordOwner,
      fordPassID: checkAttendee.fordPassID,
      fordVIN: checkAttendee.fordVIN,
      howLearn: checkAttendee.howLearn,
      _checkedIn: checkAttendee._checkedIn ? Timestamp.fromDate(checkAttendee._checkedIn) : null,
      _checkedOut: checkAttendee._checkedOut ? Timestamp.fromDate(checkAttendee._checkedOut) : null,
      _claimed: checkAttendee._claimed ? Timestamp.fromDate(checkAttendee._claimed) : null,
      _used: checkAttendee._used ? Timestamp.fromDate(checkAttendee._used) : null,
      isDupe: checkAttendee.isDupe,
      eventDate: checkAttendee.eventDate,
      moduleName: checkAttendee.moduleName,
    };
  },
  fromFirestore(snapshot, options): CheckAttendee {
    const data = snapshot.data(options);

    // Convert Firestore timestamp to Date directly
    let eventDate = undefined;
    if (data.eventDate && typeof data.eventDate.toDate === 'function') {
      eventDate = data.eventDate.toDate();
    }

    return {
      id: snapshot.id,
      ref: snapshot.ref,
      cartID: data.cartID,
      eventID: data.eventID,
      productName: data.productName,
      eventTime: data.eventTime,
      emailSent: data.emailSent,
      waiverComplete: data.waiverComplete,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      type: data.type,
      fordOwner: data.fordOwner,
      fordPassID: data.fordPassID,
      fordVIN: data.fordVIN,
      howLearn: data.howLearn,
      _checkedIn: data._checkedIn?.toDate(),
      _checkedOut: data._checkedOut?.toDate(),
      _claimed: data._claimed?.toDate(),
      _used: data._used?.toDate(),
      isDupe: data.isDupe,
      eventDate: eventDate,
      moduleName: data.moduleName,
    };
  },
};

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onUpgrade?: () => void;
  attendee: QueryDocumentSnapshot<CheckAttendee> | null;
  checkOutMode: boolean;
}

interface SurveyChoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDeviceChoice: () => void;
  attendee: QueryDocumentSnapshot<CheckAttendee> | null;
}

interface UpgradeQRDialogProps {
  isOpen: boolean;
  onClose: () => void;
  attendee: QueryDocumentSnapshot<CheckAttendee> | null;
}

interface FilterOption {
  text: string;
  value: string;
}

// Helper function to strip HTML tags from strings
const stripHtmlTags = (str: string | undefined): string => {
  return str?.replace(/<[^>]*>/g, '') || '';
};

// Flexible date parsing function that tries multiple formats
// IMPORTANT: eventTime strings are in the EVENT's timezone, not the browser's local timezone
const parseEventDate = (
  eventTime: string | undefined,
  eventDate: Date | undefined,
  timezone: string,
): moment.Moment | null => {
  // Try eventTime first with multiple formats
  if (eventTime) {
    const formats = [
      'dddd MMM DD, YYYY hh:mm a', // With year
      'dddd MMM DD, YYYY h:mm a', // With year, single digit hour
      'dddd MMM DD, hh:mm a', // Without year
      'dddd MMM DD, h:mm a', // Without year, single digit hour
      'YYYY-MM-DD HH:mm', // ISO-like
      'MM/DD/YYYY hh:mm a', // US format
    ];

    for (const format of formats) {
      // Parse the string AS the event timezone (not browser local time)
      const parsed = moment.tz(eventTime, format, true, timezone);
      if (parsed.isValid()) {
        return parsed;
      }
    }

    // Try parsing without strict mode as fallback, still in event timezone
    const looseParsed = moment.tz(eventTime, timezone);
    if (looseParsed.isValid()) {
      return looseParsed;
    }
  }

  // Fall back to eventDate
  if (eventDate) {
    const parsed = moment.utc(eventDate).tz(timezone);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  return null;
};

function CheckOutScreen() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  // Bootcamp state
  const [bootcamp, setBootcamp] = useState<Bootcamp | null>(null);
  const [bootcampLoading, setBootcampLoading] = useState(true);
  const [activeBootcamps, setActiveBootcamps] = useState<Bootcamp[]>([]);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [surveyChoiceDialogVisible, setSurveyChoiceDialogVisible] = useState(false);
  const [upgradeDialogVisible, setUpgradeDialogVisible] = useState(false);
  const [checkOutMode, setCheckOutMode] = useState(true);
  const [attendeesRef, setAttendeesRef] = useState<CollectionReference>();
  const [attendeesQuery, setAttendeesQuery] = useState<Query>();
  const [selectedAttendee, setSelectedAttendee] = useState<QueryDocumentSnapshot<CheckAttendee> | null>(null);
  const [user, userLoading, userError] = useAuthState(auth);
  const [attendees, attendeesLoading, attendeesError] = useCollection(attendeesQuery);
  const isMounted = useRef(true);

  // Filter state
  const [dateOptions, setDateOptions] = useState<FilterOption[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('all');
  const [selectedModule, setSelectedModule] = useState<string>('all');
  const [timeOptions, setTimeOptions] = useState<FilterOption[]>([]);
  const [selectedTime, setSelectedTime] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredAttendees, setFilteredAttendees] = useState<QueryDocumentSnapshot<CheckAttendee>[]>([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);

  // Derived values from bootcamp - use bootcamp.id for eventID (matches what registration writes)
  const eventID = bootcamp?.id || '';
  const timezone = bootcamp?.location?.timezone || 'America/New_York';

  // Dynamic module options from bootcamp products (strip HTML tags for display)
  const moduleOptions = useMemo(() => {
    if (!bootcamp) return [{ text: 'All Modules', value: 'all' }];
    return [
      { text: 'All Modules', value: 'all' },
      ...Object.values(bootcamp.products).map(p => ({
        text: stripHtmlTags(p.name),
        value: stripHtmlTags(p.name),
      })),
    ];
  }, [bootcamp]);

  // Helper to get plain product name from annotated name stored on attendee
  const getPlainProductName = useMemo(() => {
    if (!bootcamp) return (name: string | undefined) => stripHtmlTags(name);
    return (name: string | undefined) => {
      if (!name) return 'Not specified';
      const product = Object.values(bootcamp.products).find(p => p.annotatedName === name || p.name === name);
      return product ? stripHtmlTags(product.name) : stripHtmlTags(name);
    };
  }, [bootcamp]);

  // Fetch bootcamp data
  useEffect(() => {
    if (!slug) return;

    setBootcampLoading(true);
    getBootcampBySlug(slug)
      .then(data => {
        setBootcamp(data);
        setBootcampLoading(false);
      })
      .catch(err => {
        console.error('Error fetching bootcamp:', err);
        setBootcampLoading(false);
      });
  }, [slug]);

  // Fetch active bootcamps for "Did you mean?" suggestions
  useEffect(() => {
    if (!bootcamp && !bootcampLoading) {
      getActiveBootcamps().then(setActiveBootcamps).catch(console.error);
    }
  }, [bootcamp, bootcampLoading]);

  useEffect(() => {
    if (attendeesError) console.error(attendeesError);
  }, [attendeesError]);

  useEffect(() => {
    if (userError) console.error(userError);
  }, [userError]);

  useEffect(() => {
    console.log('attendeesLoading', attendeesLoading);
    if (!attendeesLoading && attendees) {
      processAttendees();
    }
  }, [attendeesLoading, attendees, timezone, selectedDate, selectedModule]);

  useEffect(() => {
    if (userLoading) return;

    // see if the user is logged in
    console.log('user', user);

    if (!user) {
      navigate('./login');
    }

    setAttendeesRef(collection(db, 'attendees'));
  }, [userLoading, navigate, user]);

  // Load date options when bootcamp data is available
  useEffect(() => {
    if (!userLoading && attendeesRef && eventID) {
      setIsLoadingFilters(true);

      // First create a query to get all attendees for this event to populate filters
      // Don't use complex orderBy clauses for getting date options, they may require indices
      const allAttendeesQuery = query(attendeesRef, where('eventID', '==', eventID)).withConverter(checkUserConverter);

      // Get all attendees for this event to populate date options
      getDocs(allAttendeesQuery)
        .then(snapshot => {
          console.log(`Found ${snapshot.size} total attendees for ${eventID} event`);

          // Process date options from all attendees regardless of check-out status
          const dateOptionsTemp: FilterOption[] = [{ text: 'All Dates', value: 'all' }];
          const datesSet = new Set<string>();

          snapshot.forEach(doc => {
            const attendee = doc.data();
            const attendeeDate = parseEventDate(attendee.eventTime, attendee.eventDate, timezone);

            if (attendeeDate && attendeeDate.isValid()) {
              const dateStr = attendeeDate.format('YYYY-MM-DD');
              const displayDate = attendeeDate.format('dddd, MMM D, YYYY');
              if (!datesSet.has(dateStr)) {
                datesSet.add(dateStr);
                dateOptionsTemp.push({ text: displayDate, value: dateStr });
              }
            }
          });

          // Sort dates chronologically (earliest first)
          dateOptionsTemp.sort((a, b) => {
            if (a.value === 'all') return -1;
            if (b.value === 'all') return 1;
            return a.value.localeCompare(b.value); // This already sorts ascending (earliest first)
          });

          setDateOptions(dateOptionsTemp);
          setIsLoadingFilters(false);
        })
        .catch(error => {
          console.error('Error fetching attendees:', error);
          setIsLoadingFilters(false);
        });
    }
  }, [userLoading, attendeesRef, eventID]);

  // Update the query when checkOutMode or filters change
  useEffect(() => {
    if (!userLoading && attendeesRef) {
      // Cannot use orderBy with non-equality filter on same field
      // We need a different approach for checking out vs viewing checked out
      if (checkOutMode) {
        // For check-out: show users checked in but not checked out
        setAttendeesQuery(
          query(
            attendeesRef,
            where('eventID', '==', eventID),
            where('_checkedIn', '!=', null),
            where('_checkedOut', '==', null),
            orderBy('_checkedIn', 'asc'), // Sort by check-in time (earliest first)
            orderBy('productName', 'asc'),
            orderBy('firstName', 'asc'),
            orderBy('lastName', 'asc'),
          ).withConverter(checkUserConverter),
        );
      } else {
        // For viewing checked out: show users already checked out
        // Sort by check-out time as requested
        setAttendeesQuery(
          query(
            attendeesRef,
            where('eventID', '==', eventID),
            where('_checkedOut', '!=', null),
            orderBy('_checkedOut', 'asc'), // Sort by check-out time (earliest first)
            orderBy('productName', 'asc'),
            orderBy('firstName', 'asc'),
            orderBy('lastName', 'asc'),
          ).withConverter(checkUserConverter),
        );
      }
    }
  }, [userLoading, checkOutMode, attendeesRef, eventID, selectedDate, selectedModule, selectedTime]);

  // Update time options when date or module changes
  useEffect(() => {
    if (!attendees) return;

    const timeOptionsTemp: FilterOption[] = [{ text: 'All Times', value: 'all' }];
    const timesSet = new Set<string>();

    attendees.docs.forEach(doc => {
      const attendee = doc.data();
      const attendeeDate = parseEventDate(attendee.eventTime, attendee.eventDate, timezone);

      if (attendeeDate && attendeeDate.isValid()) {
        // Extract hour and minutes for display
        const hour = parseInt(attendeeDate.format('H'));
        const minute = parseInt(attendeeDate.format('m'));

        // Only include times for the selected date and module (or all if "all" is selected)
        const dateMatches = selectedDate === 'all' || attendeeDate.format('YYYY-MM-DD') === selectedDate;

        const moduleMatches = selectedModule === 'all' || getPlainProductName(attendee.productName) === selectedModule;

        if (dateMatches && moduleMatches) {
          // Create time key in timezone
          const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          // Format for display in AM/PM format
          const displayTime = attendeeDate.format('h:mm A');

          if (!timesSet.has(timeStr)) {
            timesSet.add(timeStr);
            timeOptionsTemp.push({ text: displayTime, value: timeStr });
          }
        }
      }
    });

    // Sort times chronologically
    timeOptionsTemp.sort((a, b) => {
      if (a.value === 'all') return -1;
      if (b.value === 'all') return 1;
      return a.value.localeCompare(b.value);
    });

    setTimeOptions(timeOptionsTemp);
    setSelectedTime('all'); // Reset time selection when date or module changes
  }, [attendees, selectedDate, selectedModule, timezone]);

  // Helper function to strip non-digit characters from phone numbers
  const stripPhoneNumber = (phone: string): string => {
    return phone ? phone.replace(/\D/g, '') : '';
  };

  // Filter attendees based on selections - consolidated filtering logic
  useEffect(() => {
    if (!attendees) return;

    const searchTermLower = searchTerm.toLowerCase().trim();

    const filtered = attendees.docs.filter(doc => {
      const attendee = doc.data();
      const attendeeDate = parseEventDate(attendee.eventTime, attendee.eventDate, timezone);

      // If no valid date, include only if all dates selected
      if (!attendeeDate || !attendeeDate.isValid()) {
        return selectedDate === 'all';
      }

      // Filter by date
      if (selectedDate !== 'all') {
        const attendeeDateStr = attendeeDate.format('YYYY-MM-DD');
        if (attendeeDateStr !== selectedDate) return false;
      }

      // Filter by module - compare stripped HTML tags
      if (selectedModule !== 'all') {
        const moduleMatches = getPlainProductName(attendee.productName) === selectedModule;
        if (!moduleMatches) return false;
      }

      // Filter by time
      if (selectedTime !== 'all') {
        const hour = parseInt(attendeeDate.format('H'));
        const minute = parseInt(attendeeDate.format('m'));
        const attendeeTimeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        if (attendeeTimeStr !== selectedTime) return false;
      }

      // Filter by search term if one is provided
      if (searchTermLower) {
        const fullName = `${attendee.firstName || ''} ${attendee.lastName || ''}`.toLowerCase();
        const email = (attendee.email || '').toLowerCase();
        const phone = stripPhoneNumber(attendee.phone || '');
        const searchTermDigits = stripPhoneNumber(searchTermLower);

        // Check if the search term is found in name, email, or phone
        const nameMatch = fullName.includes(searchTermLower);
        const emailMatch = email.includes(searchTermLower);
        const phoneMatch = searchTermDigits.length > 0 && phone.includes(searchTermDigits);

        if (!(nameMatch || emailMatch || phoneMatch)) {
          return false;
        }
      }

      return true;
    });

    setFilteredAttendees(filtered as QueryDocumentSnapshot<CheckAttendee>[]);
  }, [attendees, selectedDate, selectedModule, selectedTime, searchTerm, timezone]);

  // Process attendees to extract data for filters (time options only - main filtering is in useEffect)
  const processAttendees = () => {
    if (!attendees) return;

    // Update time options based on current selections
    const timeOptionsTemp: FilterOption[] = [{ text: 'All Times', value: 'all' }];
    const timesSet = new Set<string>();

    attendees.docs.forEach(doc => {
      const attendee = doc.data();
      const attendeeDate = parseEventDate(attendee.eventTime, attendee.eventDate, timezone);

      if (attendeeDate && attendeeDate.isValid()) {
        // Only include times for the selected date and module
        const dateMatches = selectedDate === 'all' || attendeeDate.format('YYYY-MM-DD') === selectedDate;
        const moduleMatches = selectedModule === 'all' || getPlainProductName(attendee.productName) === selectedModule;

        if (dateMatches && moduleMatches) {
          const hour = parseInt(attendeeDate.format('H'));
          const minute = parseInt(attendeeDate.format('m'));
          const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          const displayTime = attendeeDate.format('h:mm A');
          if (!timesSet.has(timeStr)) {
            timesSet.add(timeStr);
            timeOptionsTemp.push({ text: displayTime, value: timeStr });
          }
        }
      }
    });

    // Sort times chronologically
    timeOptionsTemp.sort((a, b) => {
      if (a.value === 'all') return -1;
      if (b.value === 'all') return 1;
      return a.value.localeCompare(b.value);
    });

    setTimeOptions(timeOptionsTemp);
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Conditional returns AFTER all hooks (Rules of Hooks compliance)
  // Show loading while fetching bootcamp
  if (bootcampLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>Loading...</div>
      </div>
    );
  }

  // Show not found with suggestions if bootcamp doesn't exist
  if (!bootcamp) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>
          <Typography displayStyle="display-2-small-regular">Bootcamp "{slug}" not found</Typography>
          <br />
          <Typography displayStyle="body-1-regular">Did you mean?</Typography>
          <div className={styles.buttonsContainer}>
            {activeBootcamps.map(b => (
              <Button key={b.slug} onClick={() => (window.location.href = `/bootcamp/${b.slug}/out`)}>
                {b.name}
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const AttendeeListItem = ({ doc, index }: { doc: QueryDocumentSnapshot<CheckAttendee>; index: number }) => {
    const attendee: CheckAttendee = doc.data();
    const attendeeDate = parseEventDate(attendee.eventTime, attendee.eventDate, timezone);

    return (
      <div className={`${styles.attendeeRow} ${index % 2 ? styles.attendeeRowEven : styles.attendeeRowOdd}`}>
        <div className={styles.attendeeInfo}>
          <h2 className={styles.attendeeName}>
            {attendee.firstName} {attendee.lastName}
          </h2>
          <div className={styles.attendeeDetails}>
            <div>
              <strong>Email/Phone:</strong> {attendee.email} {attendee.phone}
            </div>
            <div>
              <strong>Module:</strong> {getPlainProductName(attendee.productName)}
            </div>
            <div>
              <strong>Date/Time:</strong>{' '}
              {attendeeDate && attendeeDate.isValid()
                ? attendeeDate.format('ddd, MMM D, YYYY h:mm A')
                : 'Date unavailable'}
            </div>
            <div>
              <strong>Learning Preference:</strong> {attendee.howLearn || 'Not specified'}
            </div>
            {attendee._checkedIn && (
              <div>
                <strong>Checked In:</strong>{' '}
                {moment(attendee._checkedIn).tz(timezone).format('ddd, MMM D, YYYY h:mm A')}
              </div>
            )}
            {attendee._checkedOut && (
              <div>
                <strong>Checked Out:</strong>{' '}
                {moment(attendee._checkedOut).tz(timezone).format('ddd, MMM D, YYYY h:mm A')}
              </div>
            )}
          </div>
        </div>
        <div className={styles.actionArea} style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div className={styles.actionButton}>
            <Button
              buttonType={checkOutMode ? 'primary' : 'secondary'}
              onClick={() => {
                setSelectedAttendee(doc as QueryDocumentSnapshot<CheckAttendee>);
                setDialogVisible(true);
              }}
              disabled={!checkOutMode && attendee._used !== null && attendee._used !== undefined}
            >
              {checkOutMode ? 'Check Out' : 'Undo Check-Out'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const handleConfirmDialog = async () => {
    if (selectedAttendee) {
      if (checkOutMode) {
        // After confirmation, show the survey choice dialog instead of checking out immediately
        setDialogVisible(false);
        setSurveyChoiceDialogVisible(true);
      } else {
        // Undo check-out - remove the check-out timestamp
        await updateDoc(selectedAttendee.ref, {
          _checkedOut: null,
        });
        setDialogVisible(false);
        setSelectedAttendee(null);
      }
    }
  };

  const handleSurveyOnDevice = () => {
    if (selectedAttendee) {
      // Update the document with check-out timestamp
      updateDoc(selectedAttendee.ref, {
        _checkedOut: Timestamp.now(),
      }).then(() => {
        // Redirect to post-survey page
        navigate(
          `/post?attendeeID=${selectedAttendee.id}&redirect=${encodeURIComponent(
            window.location.pathname + window.location.search,
          )}`,
        );
      });
    }
    setSurveyChoiceDialogVisible(false);
    setSelectedAttendee(null);
  };

  const handleSurveyByQRCode = async () => {
    if (selectedAttendee) {
      // Set the check-out timestamp
      await updateDoc(selectedAttendee.ref, {
        _checkedOut: Timestamp.now(),
      });
    }
    setSurveyChoiceDialogVisible(false);
    setSelectedAttendee(null);
  };

  const handleUpgradeClick = () => {
    if (selectedAttendee) {
      setDialogVisible(false);
      setUpgradeDialogVisible(true);
    }
  };

  // Dialog to confirm check-out
  const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    onUpgrade,
    attendee,
    checkOutMode,
  }) => {
    if (!isOpen || !attendee) return null;
    const attendeeData = attendee.data();

    // Eligible: Towing 101, Trailer Demo, or Hands-On Trailer Backup
    // Use .includes() because productName format varies (legacy vs dynamic bootcamp)
    const pn = attendeeData.productName?.toLowerCase() || '';
    const isEligibleForUpgrade =
      pn.includes('towing 101') ||
      pn.includes('trailer backup assist') ||
      pn.includes('hands-on trailer backup');

    return (
      <Dialog
        title={checkOutMode ? 'Check Out Confirmation' : 'Undo Check Out Confirmation'}
        onClose={onClose}
        className="waiver-dialog"
        style={{ zIndex: 9999 }}
      >
        <div className="p-4">
          <Typography displayStyle="body-1-regular">
            Are you sure you want to {checkOutMode ? 'check out' : 'undo check-out for'}{' '}
            <strong>
              {attendeeData.firstName} {attendeeData.lastName}
            </strong>
            ?
          </Typography>
        </div>
        <DialogActionsBar>
          <Button buttonType="tertiary" onClick={onClose}>
            Cancel
          </Button>
          {checkOutMode && onUpgrade && isEligibleForUpgrade && (
            <Button
              buttonType="secondary"
              onClick={onUpgrade}
              disabled={attendeeData._used !== undefined && attendeeData._used !== null}
            >
              ✨Upgrade✨
            </Button>
          )}
          <Button
            buttonType={checkOutMode ? 'primary' : 'secondary'}
            onClick={onConfirm}
            disabled={checkOutMode && attendeeData._used !== undefined && attendeeData._used !== null}
          >
            Confirm
          </Button>
        </DialogActionsBar>
      </Dialog>
    );
  };

  // Dialog to choose survey method after check-out confirmation
  const SurveyChoiceDialog: React.FC<SurveyChoiceDialogProps> = ({ isOpen, onClose, onDeviceChoice, attendee }) => {
    if (!isOpen || !attendee) return null;
    const attendeeData = attendee.data();
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);

    useEffect(() => {
      if (isOpen && attendee) {
        // Generate the post-survey URL with UTM parameters for the QR code
        // Ensure the protocol is included for proper QR code scanning
        const baseUrl = `${window.location.protocol}//${window.location.host}`;
        const attendeeId = attendeeData.id;

        // Create UTM parameters
        const utmParams = new URLSearchParams({
          utm_source: 'checkout_screen',
          utm_medium: 'qr_code',
          utm_campaign: 'post_survey_completion',
          utm_content: attendeeData.eventID || 'dallas',
        }).toString();

        // Complete post-survey URL with protocol included
        const surveyUrl = `${baseUrl}/post?attendeeID=${attendeeId}&${utmParams}`;

        // Generate QR code
        import('qrcode').then(QRCode => {
          QRCode.toDataURL(surveyUrl, { width: 200, margin: 2 })
            .then((url: string) => {
              setQrCodeUrl(url);
            })
            .catch((err: Error) => {
              console.error('Error generating QR code:', err);
            });
        });
      }
    }, [isOpen, attendee, attendeeData]);

    return (
      <Dialog title="Post-Event Survey" onClose={onClose} className="waiver-dialog" style={{ zIndex: 9999 }}>
        <div className="p-4" style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '16px' }}>
            <Typography displayStyle="body-1-regular">
              <span style={{ fontWeight: 'bold' }}>
                How would {attendeeData.firstName} {attendeeData.lastName} like to complete the post-event survey?
              </span>
            </Typography>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Typography displayStyle="body-1-regular">
              They can either complete it on this device or scan a QR code to complete it on their personal device. They
              will receive an email with the post-survey upon check-out either way.
            </Typography>
          </div>

          {qrCodeUrl && (
            <div style={{ margin: '24px 0' }}>
              <img
                src={qrCodeUrl}
                alt="Post-Survey QR Code"
                style={{ width: '200px', height: '200px', margin: '0 auto' }}
              />
              <div style={{ marginTop: '8px' }}>
                <Typography displayStyle="body-1-regular">
                  Scan this QR code to complete the survey on a personal device
                </Typography>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
            <Button buttonType="tertiary" onClick={onClose}>
              Cancel Check-Out
            </Button>
            <Button buttonType="primary" onClick={onDeviceChoice}>
              Complete survey on this device
            </Button>
            <Button buttonType="secondary" onClick={handleSurveyByQRCode}>
              Check Out
            </Button>
          </div>
        </div>
      </Dialog>
    );
  };

  // New dialog for the upgrade QR code
  const UpgradeQRDialog: React.FC<UpgradeQRDialogProps> = ({ isOpen, onClose, attendee }) => {
    if (!isOpen || !attendee) return null;
    const attendeeData = attendee.data();
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [upgradeUrl, setUpgradeUrl] = useState<string | null>(null);

    useEffect(() => {
      if (isOpen && attendee) {
        // Generate the upgrade URL with attendeeID
        const baseUrl = `${window.location.protocol}//${window.location.host}`;
        const attendeeId = attendee.id;

        // Create UTM parameters
        const utmParams = new URLSearchParams({
          utm_source: 'checkout_screen',
          utm_medium: 'qr_code',
          utm_campaign: 'module_upgrade',
          utm_content: attendeeData.eventID || 'dallas',
        }).toString();

        // Complete upgrade URL with protocol included (uses mode=upgrade query param)
        const url = `${baseUrl}/bootcamp/${slug}?mode=upgrade&attendeeID=${attendeeId}&sourceProduct=${encodeURIComponent(attendeeData.productName || '')}&${utmParams}`;
        setUpgradeUrl(url);

        // Generate QR code
        import('qrcode').then(QRCode => {
          QRCode.toDataURL(url, { width: 200, margin: 2 })
            .then((dataUrl: string) => {
              setQrCodeUrl(dataUrl);
            })
            .catch((err: Error) => {
              console.error('Error generating QR code:', err);
            });
        });
      }
    }, [isOpen, attendee, attendeeData]);

    return (
      <Dialog title="Upgrade Experience" onClose={onClose} className="waiver-dialog" style={{ zIndex: 9999 }}>
        <div className="p-4" style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '16px' }}>
            <Typography displayStyle="body-1-regular">
              <span style={{ fontWeight: 'bold' }}>
                Upgrade {attendeeData.firstName} {attendeeData.lastName}'s experience
              </span>
            </Typography>
          </div>

          {qrCodeUrl && (
            <div style={{ margin: '24px 0' }}>
              <img
                src={qrCodeUrl}
                alt="Upgrade QR Code"
                style={{ width: '200px', height: '200px', margin: '0 auto' }}
              />
              <div style={{ marginTop: '8px' }}>
                <Typography displayStyle="body-1-regular">Scan to select a new module & pay</Typography>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
            <Button buttonType="primary" onClick={onClose}>
              OK
            </Button>
          </div>

          {upgradeUrl && (
            <div style={{ marginTop: '16px' }}>
              <a
                href={upgradeUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '11px', color: '#666', textDecoration: 'underline' }}
              >
                Open upgrade page
              </a>
            </div>
          )}
        </div>
      </Dialog>
    );
  };

  if (userLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles.container}>
        <div className={styles.noResultsMessage}>
          <Typography displayStyle="display-2-small-regular">Please log in to access this page.</Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <Typography displayStyle="display-2-small-regular">
          {bootcamp.name} Event: {checkOutMode ? 'Check Out Attendees' : 'View Checked Out Attendees'}
        </Typography>
      </div>

      <div className={styles.tabContainer}>
        <div
          className="segmented-control flex mb-4"
          style={{ border: '1px solid #0066B2', borderRadius: '4px', overflow: 'hidden' }}
        >
          <div
            className={`segment ${checkOutMode ? 'active' : ''}`}
            style={{
              flex: 1,
              padding: '10px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: checkOutMode ? '#0066B2' : 'transparent',
              color: checkOutMode ? 'white' : '#0066B2',
              fontWeight: 500,
              transition: 'background-color 0.2s, color 0.2s',
            }}
            onClick={() => setCheckOutMode(true)}
          >
            Check Out
          </div>
          <div
            className={`segment ${!checkOutMode ? 'active' : ''}`}
            style={{
              flex: 1,
              padding: '10px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: !checkOutMode ? '#0066B2' : 'transparent',
              color: !checkOutMode ? 'white' : '#0066B2',
              fontWeight: 500,
              transition: 'background-color 0.2s, color 0.2s',
            }}
            onClick={() => setCheckOutMode(false)}
          >
            View Checked Out
          </div>
        </div>
      </div>

      <div className={styles.filtersContainer ? styles.filtersContainer : 'mb-6 p-4 border rounded'}>
        <Typography displayStyle="body-1-regular">Filter Attendees</Typography>

        {/* Search Input */}
        <div className="mb-4 mt-2">
          <Typography displayStyle="body-1-regular">Search</Typography>
          <div className="mt-2">
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder="Search by name, email, or phone"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 mb-2">
          {/* Date Filter */}
          <div>
            <Typography displayStyle="body-1-regular">Date</Typography>
            <div className="mt-2">
              <select
                className="w-full p-2 border rounded"
                value={selectedDate}
                disabled={isLoadingFilters}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedDate(e.target.value)}
              >
                {dateOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Module Filter */}
          <div>
            <Typography displayStyle="body-1-regular">Module</Typography>
            <div className="mt-2">
              <select
                className="w-full p-2 border rounded"
                value={selectedModule}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedModule(e.target.value)}
              >
                {moduleOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Time Filter */}
          <div>
            <Typography displayStyle="body-1-regular">Time</Typography>
            <div className="mt-2">
              <select
                className="w-full p-2 border rounded"
                value={selectedTime}
                disabled={timeOptions.length <= 1}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTime(e.target.value)}
              >
                {timeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="mt-2">
          <Typography displayStyle="body-1-regular">
            Showing {filteredAttendees?.length || 0} attendee{(filteredAttendees?.length || 0) !== 1 ? 's' : ''}
          </Typography>
        </div>
      </div>

      {attendeesLoading ? (
        <div className={styles.loadingSpinner}>
          <Typography displayStyle="body-1-regular">Loading attendees...</Typography>
        </div>
      ) : attendeesError ? (
        <div className={styles.noResultsMessage}>
          <Typography displayStyle="body-1-regular">Error loading attendees: {attendeesError.message}</Typography>
        </div>
      ) : filteredAttendees.length === 0 ? (
        <div className={styles.noResultsMessage}>
          <Typography displayStyle="body-1-regular">No attendees found matching the selected filters.</Typography>
        </div>
      ) : (
        <div className={styles.listContainer}>
          {filteredAttendees.map((doc, index) => (
            <AttendeeListItem key={doc.id} doc={doc} index={index} />
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={dialogVisible}
        onClose={() => setDialogVisible(false)}
        onConfirm={handleConfirmDialog}
        onUpgrade={handleUpgradeClick}
        attendee={selectedAttendee}
        checkOutMode={checkOutMode}
      />

      <SurveyChoiceDialog
        isOpen={surveyChoiceDialogVisible}
        onClose={() => setSurveyChoiceDialogVisible(false)}
        onDeviceChoice={handleSurveyOnDevice}
        attendee={selectedAttendee}
      />

      {upgradeDialogVisible && selectedAttendee && (
        <UpgradeQRDialog
          isOpen={upgradeDialogVisible}
          onClose={() => {
            setUpgradeDialogVisible(false);
            setSelectedAttendee(null);
          }}
          attendee={selectedAttendee}
        />
      )}
    </div>
  );
}

export default CheckOutScreen;
